import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./CoursesPage.css";
import { coursesApi, enrollmentsApi, getToken } from "../../../../api/coursesApi.js";
import Login from "../../Modal/Login/Login.jsx";

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   HELPERS
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function toStars(value) {
  const safe = Number(value || 0);
  return Array.from({ length: 5 }, (_, i) =>
    i + 1 <= Math.round(safe) ? "â˜…" : "â˜†"
  ).join("");
}

function readCurrentUserId() {
  try {
    const raw = localStorage.getItem("th_user") || localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = Number(parsed?.id);
    return Number.isFinite(id) ? id : null;
  } catch { return null; }
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const mos  = Math.floor(days / 30);
  const yrs  = Math.floor(days / 365);
  if (yrs  > 0) return `${yrs} year${yrs > 1 ? "s" : ""} ago`;
  if (mos  > 0) return `${mos} month${mos > 1 ? "s" : ""} ago`;
  if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (hrs  > 0) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  return `${mins} min${mins !== 1 ? "s" : ""} ago`;
}

function fmtDuration(sec) {
  if (!sec) return "0:00";
  const s = Number(sec), h = Math.floor(s / 3600),
    m = Math.floor((s % 3600) / 60), ss = Math.floor(s % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${m}:${String(ss).padStart(2, "0")}`;
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = String(url).trim().match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

function isPdfAsset(url) {
  if (!url) return false;
  const cleaned = String(url).trim().split("?")[0].toLowerCase();
  return cleaned.endsWith(".pdf");
}

function resolveVideoType(video) {
  const type = String(video?.video_type || "").toLowerCase();
  if (type === "youtube") return "youtube";
  if (type === "upload")  return "upload";
  if (type === "link")    return "link";
  const url = String(video?.video_url || "");
  if (!url) return "";
  if (getYouTubeId(url)) return "youtube";
  if (url.startsWith("/uploads/") || url.startsWith("blob:") || url.startsWith("data:")) return "upload";
  if (url.startsWith("http")) return "link";
  return "";
}

function getAuthToken() {
  return localStorage.getItem("th_token") || localStorage.getItem("token") || "";
}

function getInstructorField(instructor, field) {
  if (!instructor) return "";
  if (typeof instructor === "string") return field === "name" ? instructor : "";
  if (typeof instructor === "object" && !Array.isArray(instructor)) return String(instructor[field] || "");
  return "";
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   STAR PICKER
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function StarPicker({ value, onChange, disabled }) {
  const [hovered, setHovered] = useState(0);
  const labels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];
  return (
    <div className="vp-star-picker">
      {[1, 2, 3, 4, 5].map((star) => (
        <button key={star} type="button"
          className={`vp-star-btn ${star <= (hovered || value) ? "filled" : ""}`}
          onMouseEnter={() => !disabled && setHovered(star)}
          onMouseLeave={() => !disabled && setHovered(0)}
          onClick={() => !disabled && onChange(star)}
          disabled={disabled}>â˜…</button>
      ))}
      {(hovered || value) > 0 && <span className="vp-star-label">{labels[hovered || value]}</span>}
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   PDF VIEWER â€” PDF.js canvas rendering (no iframe blank bug)
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function PdfViewer({ pdfUrl, filename }) {
  const containerRef = useRef(null);
  const [status, setStatus]     = useState("idle");
  const [pageCount, setPageCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const pdfDocRef    = useRef(null);
  const renderingRef = useRef(false);

  /* Load PDF.js from CDN once */
  const loadPdfJs = () =>
    new Promise((resolve, reject) => {
      if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      s.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      };
      s.onerror = () => reject(new Error("Failed to load PDF.js"));
      document.head.appendChild(s);
    });

  /* Render every page onto a canvas */
  const renderAll = useCallback(async (pdfDoc) => {
    if (!containerRef.current || renderingRef.current) return;
    renderingRef.current = true;
    containerRef.current.innerHTML = "";

    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const page       = await pdfDoc.getPage(pageNum);
      const cw         = containerRef.current?.offsetWidth || 760;
      const viewport   = page.getViewport({ scale: 1 });
      const scale      = (cw - 32) / viewport.width;
      const scaledVP   = page.getViewport({ scale });
      const dpr        = Math.min(window.devicePixelRatio || 1, 2);

      /* Page wrapper */
      const wrap = document.createElement("div");
      wrap.className = "vn-pdf-page-wrap";

      /* Badge */
      const badge = document.createElement("div");
      badge.className   = "vn-pdf-page-badge";
      badge.textContent = `${pageNum} / ${pdfDoc.numPages}`;

      /* Canvas */
      const canvas    = document.createElement("canvas");
      canvas.width    = scaledVP.width  * dpr;
      canvas.height   = scaledVP.height * dpr;
      canvas.style.width  = "100%";
      canvas.style.height = scaledVP.height + "px";
      canvas.style.display = "block";
      const ctx2 = canvas.getContext("2d");
      ctx2.scale(dpr, dpr);

      await page.render({ canvasContext: ctx2, viewport: scaledVP }).promise;

      wrap.appendChild(canvas);
      wrap.appendChild(badge);
      containerRef.current?.appendChild(wrap);
    }
    renderingRef.current = false;
  }, []);

  /* Load + render */
  useEffect(() => {
    if (!pdfUrl) { setStatus("error"); setErrorMsg("No PDF URL."); return; }
    let cancelled = false;
    setStatus("loading"); setPageCount(0); setErrorMsg("");
    if (containerRef.current) containerRef.current.innerHTML = "";

    (async () => {
      try {
        const lib  = await loadPdfJs();
        if (cancelled) return;
        const doc  = await lib.getDocument(pdfUrl).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setPageCount(doc.numPages);
        setStatus("ready");
        await renderAll(doc);
      } catch (err) {
        if (!cancelled) { setStatus("error"); setErrorMsg(err?.message || "Could not load PDF."); }
      }
    })();

    return () => { cancelled = true; };
  }, [pdfUrl, renderAll]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = pdfUrl; a.download = filename || "notes.pdf";
    a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="vn-pdf-wrap">
      {/* Top bar */}
      <div className="vn-pdf-topbar">
        <div className="vn-pdf-title-row">
          <span className="vn-pdf-icon">ðŸ“„</span>
          <span className="vn-pdf-filename">{filename || "Lesson Notes PDF"}</span>
          {status === "ready" && pageCount > 0 && (
            <span className="vn-pdf-pages">{pageCount} pages</span>
          )}
        </div>
        <button className="vn-pdf-download-btn" onClick={handleDownload} title="Download PDF">
          â¬‡ Download
        </button>
      </div>

      {/* Viewer */}
      <div className="vn-pdf-viewer">
        {status === "loading" && (
          <div className="vn-pdf-status">
            <div className="vn-pdf-spinner" />
            <p className="vn-pdf-status-text">Loading PDFâ€¦</p>
          </div>
        )}
        {status === "error" && (
          <div className="vn-pdf-status">
            <div className="vn-pdf-error-icon">!</div>
            <p className="vn-pdf-status-text" style={{ color: "#dc2626" }}>{errorMsg}</p>
            <p style={{ fontSize: "0.76rem", color: "#888", maxWidth: 400, textAlign: "center" }}>
              Make sure the PDF URL is correct and the server allows cross-origin access.
            </p>
            <button className="vn-pdf-retry-btn" onClick={() => setStatus("idle")}>Retry</button>
          </div>
        )}
        {/* Canvas pages mount here */}
        <div ref={containerRef} style={{ display: status === "ready" ? "contents" : "none" }} />
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   VIDEO PLAYER
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function VideoPlayer({ video, poster, onEnded, autoPlay = false }) {
  const videoRef     = useRef(null);
  const containerRef = useRef(null);
  const progressRef  = useRef(null);
  const volumeRef    = useRef(null);
  const hideTimer    = useRef(null);

  const [playing, setPlaying]       = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]     = useState(0);
  const [volume, setVolume]         = useState(1);
  const [muted, setMuted]           = useState(false);
  const [speed, setSpeed]           = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [showCtrl, setShowCtrl]     = useState(true);
  const [showSpeed, setShowSpeed]   = useState(false);
  const [liked, setLiked]           = useState(false);
  const [likes, setLikes]           = useState(() => Math.floor(Math.random() * 900 + 100));
  const [buffered, setBuffered]     = useState(0);
  const [showShareTip, setShowShareTip] = useState(false);
  const [seeking, setSeeking]       = useState(false);
  const [videoError, setVideoError] = useState("");

  const vType    = resolveVideoType(video);
  const videoUrl = String(video?.video_url || "");
  const ytId     = vType === "youtube" ? getYouTubeId(videoUrl) : null;
  const isNative = vType === "upload" || vType === "link";

  const resetHideTimer = useCallback(() => {
    setShowCtrl(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => { if (playing) setShowCtrl(false); }, 3000);
  }, [playing]);

  useEffect(() => { resetHideTimer(); return () => clearTimeout(hideTimer.current); }, [playing, resetHideTimer]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isNative) return;
    setVideoError(""); setCurrentTime(0); setDuration(0); setPlaying(false); setBuffered(0);
    if (!videoUrl) { v.removeAttribute("src"); v.load(); return; }
    v.src = videoUrl; v.load();
    if (autoPlay) v.play().catch((e) => console.warn("Autoplay blocked:", e.message));
  }, [videoUrl, autoPlay, isNative]);

  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = speed; }, [speed]);
  useEffect(() => {
    const fn = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", fn);
    return () => document.removeEventListener("fullscreenchange", fn);
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v || !isNative) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
    resetHideTimer();
  };
  const seek = (e) => {
    const v = videoRef.current, bar = progressRef.current;
    if (!v || !bar || !duration) return;
    const pct = Math.min(1, Math.max(0, (e.clientX - bar.getBoundingClientRect().left) / bar.offsetWidth));
    v.currentTime = pct * duration; setCurrentTime(pct * duration);
  };
  const changeVolume = (e) => {
    const bar = volumeRef.current; if (!bar) return;
    const pct = Math.min(1, Math.max(0, (e.clientX - bar.getBoundingClientRect().left) / bar.offsetWidth));
    setVolume(pct); setMuted(pct === 0);
    if (videoRef.current) { videoRef.current.volume = pct; videoRef.current.muted = pct === 0; }
  };
  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    const next = !muted; setMuted(next); v.muted = next;
    if (!next && volume === 0) { setVolume(0.5); v.volume = 0.5; }
  };
  const toggleFullscreen = () => {
    const el = containerRef.current; if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen().catch(() => {});
    else document.exitFullscreen();
  };
  const handleLike = () => { setLiked((l) => { setLikes((c) => l ? c - 1 : c + 1); return !l; }); };
  const handleShare = () => {
    navigator.clipboard?.writeText(window.location.href).catch(() => {});
    setShowShareTip(true); setTimeout(() => setShowShareTip(false), 2000);
  };
  const volIcon = muted || volume === 0 ? "ðŸ”‡" : volume < 0.5 ? "ðŸ”‰" : "ðŸ”Š";
  const onProgress = () => {
    const v = videoRef.current;
    if (!v || !v.buffered.length || !v.duration) return;
    setBuffered((v.buffered.end(v.buffered.length - 1) / v.duration) * 100);
  };
  const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

  /* No source */
  if (!videoUrl) {
    return (
      <div ref={containerRef} className="vp-container"
        style={{ background: "#111", display: "flex", alignItems: "center", justifyContent: "center", aspectRatio: "16/9", flexDirection: "column", gap: 14, position: "relative" }}>
        {poster && <img src={poster} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.10 }} />}
        <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 14, opacity: 0.3 }}>â–¶</div>
          <p style={{ fontSize: 15, fontWeight: 700, color: "rgba(255,255,255,.6)" }}>No video for this lesson yet</p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,.3)", marginTop: 6 }}>Check back soon</p>
        </div>
      </div>
    );
  }

  /* YouTube iframe */
  if (vType === "youtube" && ytId) {
    return (
      <div ref={containerRef} className="vp-container" style={{ position: "relative", aspectRatio: "16/9", background: "#000" }}>
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1&autoplay=${autoPlay ? 1 : 0}`}
          title={video?.title || "Course Video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
        />
      </div>
    );
  }

  /* Native HTML5 */
  return (
    <div ref={containerRef}
      className={`vp-container ${fullscreen ? "vp-fullscreen" : ""}`}
      onMouseMove={resetHideTimer}
      onMouseLeave={() => playing && setShowCtrl(false)}
      onClick={togglePlay}>

      <video ref={videoRef} poster={poster || ""} className="vp-video"
        onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
        onDurationChange={() => setDuration(videoRef.current?.duration || 0)}
        onProgress={onProgress}
        onEnded={() => { setPlaying(false); onEnded?.(); }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onError={(e) => {
          const code = e.target?.error?.code;
          const msgs = { 1: "Loading aborted.", 2: "Network error.", 3: "Decoding failed.", 4: "Format not supported." };
          setVideoError(msgs[code] || `Playback error (code ${code})`);
          setPlaying(false);
        }} />

      {videoError && (
        <div style={{ position: "absolute", inset: 0, zIndex: 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, background: "rgba(0,0,0,.9)", color: "#fff", padding: 28, textAlign: "center" }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 40 }}>âš ï¸</div>
          <p style={{ fontSize: 14, fontWeight: 700 }}>{videoError}</p>
          <p style={{ fontSize: 11, opacity: .4, wordBreak: "break-all", maxWidth: 480 }}>URL: {videoUrl}</p>
          <button onClick={() => { setVideoError(""); videoRef.current?.load(); }}
            style={{ padding: "8px 24px", borderRadius: 999, background: "#263012", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            Retry
          </button>
        </div>
      )}

      {!playing && !videoError && (
        <div className="vp-play-overlay" onClick={(e) => { e.stopPropagation(); togglePlay(); }}>
          <div className="vp-play-circle">â–¶</div>
        </div>
      )}

      <div className={`vp-controls ${showCtrl ? "vp-controls--visible" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div ref={progressRef} className="vp-progress-track"
          onClick={seek}
          onMouseDown={(e) => { setSeeking(true); seek(e); }}
          onMouseMove={(e) => seeking && seek(e)}
          onMouseUp={() => setSeeking(false)}>
          <div className="vp-progress-buffered" style={{ width: `${buffered}%` }} />
          <div className="vp-progress-fill" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
          <div className="vp-progress-thumb" style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
        </div>
        <div className="vp-ctrl-row">
          <div className="vp-ctrl-left">
            <button className="vp-ctrl-btn" onClick={togglePlay}>{playing ? "â¸" : "â–¶"}</button>
            <button className="vp-ctrl-btn vp-skip-btn" onClick={() => { const v = videoRef.current; if (v) v.currentTime = Math.max(0, v.currentTime - 10); }}>â®</button>
            <button className="vp-ctrl-btn vp-skip-btn" onClick={() => { const v = videoRef.current; if (v) v.currentTime = Math.min(duration, v.currentTime + 10); }}>â­</button>
            <button className="vp-ctrl-btn" onClick={toggleMute}>{volIcon}</button>
            <div ref={volumeRef} className="vp-volume-bar" onClick={changeVolume}>
              <div className="vp-volume-fill" style={{ width: `${muted ? 0 : volume * 100}%` }} />
            </div>
            <span className="vp-time">{fmtDuration(currentTime)} / {fmtDuration(duration)}</span>
          </div>
          <div className="vp-ctrl-right">
            <button className={`vp-ctrl-btn vp-like-btn ${liked ? "vp-liked" : ""}`} onClick={handleLike}>
              ðŸ‘ <span className="vp-like-count">{likes}</span>
            </button>
            <div style={{ position: "relative" }}>
              <button className="vp-ctrl-btn" onClick={handleShare}>ðŸ“¤</button>
              {showShareTip && <div className="vp-share-tip">Link copied!</div>}
            </div>
            <div className="vp-speed-wrap">
              <button className="vp-ctrl-btn vp-speed-trigger" onClick={(e) => { e.stopPropagation(); setShowSpeed(s => !s); }}>{speed}Ã—</button>
              {showSpeed && (
                <div className="vp-speed-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="vp-speed-title">Speed</div>
                  {SPEEDS.map((s) => (
                    <button key={s} className={`vp-speed-opt ${speed === s ? "active" : ""}`}
                      onClick={() => { setSpeed(s); setShowSpeed(false); }}>
                      {s === 1 ? "Normal" : `${s}Ã—`}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="vp-ctrl-btn" onClick={toggleFullscreen}>â›¶</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   VIDEO QUEUE ITEM
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function VideoQueueItem({ video, index, active, onSelect, courseBanner }) {
  const vType   = resolveVideoType(video);
  const thumbSrc = video.thumbnail || courseBanner || null;
  const typeDot =
    vType === "youtube" ? { bg: "rgba(200,0,0,.85)", label: "YT" }
    : vType === "upload" ? { bg: "rgba(18,22,10,.85)", label: "MP4" }
    : vType === "link"   ? { bg: "rgba(29,78,216,.85)", label: "URL" }
    : null;

  return (
    <button
      className={`vq-item ${active ? "vq-item--active" : ""}`}
      onClick={() => onSelect(video)}
      title={video.title || `Lesson ${index + 1}`}>

      <div className="vq-thumb-wrap">
        {thumbSrc ? (
          <img src={thumbSrc} alt={video.title || ""} className="vq-thumb"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
        ) : null}
        <div className="vq-thumb-placeholder" style={{ display: thumbSrc ? "none" : "flex" }}>
          <span>{index + 1}</span>
        </div>
        {active ? (
          <div className="vq-playing-badge">â–¶ Now playing</div>
        ) : typeDot ? (
          <div className="vq-playing-badge" style={{ background: typeDot.bg }}>{typeDot.label}</div>
        ) : null}
        {video.duration_seconds > 0 && (
          <span className="vq-duration">{fmtDuration(video.duration_seconds)}</span>
        )}
      </div>

      <div className="vq-info">
        <p className="vq-title">{video.title || `Lesson ${index + 1}`}</p>
        <div className="vq-meta">
          {video.created_at && <span className="vq-time">{formatTimeAgo(video.created_at)}</span>}
          {video.views > 0 && <span className="vq-views">{Number(video.views).toLocaleString("en-IN")} views</span>}
        </div>
        {video.rating > 0 && (
          <div className="vq-stars">
            <span className="vq-star-glyph">{toStars(video.rating)}</span>
            <span className="vq-star-val">{Number(video.rating).toFixed(1)}</span>
          </div>
        )}
      </div>

      <div className="vq-index">{index + 1}</div>
    </button>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   NOTES SECTION
   â€” Tabs render ONLY when content exists
   â€” PDF tab uses PDF.js canvas viewer (no blank iframe)
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function NotesSection({ video, course }) {
  const notes    = (video?.notes       || course?.notes       || "").trim();
  const uploadedPdf = isPdfAsset(video?.video_url) ? String(video.video_url).trim() : "";
  const pdfUrl   = (video?.notes_pdf   || course?.notes_pdf   || uploadedPdf || "").trim();
  const desc     = (video?.description || course?.description || "No description provided for this lesson.").trim();
  const pdfName  = video?.title ? `${video.title} - Notes.pdf` : "Lesson Notes.pdf";

  const instructorName   = getInstructorField(course?.instructor, "name");
  const instructorTitle  = getInstructorField(course?.instructor, "title") || "Course Instructor";
  const instructorAvatar = getInstructorField(course?.instructor, "avatar");

  const tabs = [
    { id: "overview", label: "Overview", always: true },
    { id: "notes", label: "Lesson Notes", show: !!notes || !!pdfUrl },
  ].filter((t) => t.always || t.show);

  const [activeTab, setActiveTab] = useState(() => (notes || pdfUrl ? "notes" : "overview"));

  useEffect(() => {
    setActiveTab(notes || pdfUrl ? "notes" : "overview");
  }, [video?.id, notes, pdfUrl]);

  const validTab = tabs.find((t) => t.id === activeTab) ? activeTab : "overview";

  return (
    <div className="vn-root">
      <div className="vn-tabs">
        {tabs.map((tab) => (
          <button key={tab.id}
            className={`vn-tab ${validTab === tab.id ? "vn-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="vn-body">
        {validTab === "overview" && (
          <div>
            <h3 className="vn-lesson-title">{String(video?.title || course?.title || "")}</h3>
            <p className="vn-desc-text">{desc}</p>
            {instructorName && (
              <div className="vn-instructor">
                <div className="vn-inst-avatar">
                  {instructorAvatar
                    ? <img src={instructorAvatar} alt={instructorName} className="vn-inst-avatar-img" />
                    : instructorName[0]?.toUpperCase() || "I"}
                </div>
                <div>
                  <div className="vn-inst-name">{instructorName}</div>
                  <div className="vn-inst-label">{instructorTitle}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {validTab === "notes" && (
          <div className="vn-notes-wrap">
            {notes ? (
              <div
                className="vn-notes-content"
                dangerouslySetInnerHTML={{ __html: notes.replace(/\n/g, "<br/>") }}
              />
            ) : (
              <div className="vn-empty">
                <div className="vn-empty-icon">i</div>
                <p>Written notes are not available for this lesson.</p>
              </div>
            )}

            {pdfUrl && (
              <div className="vn-pdf-panel">
                <div className="vn-pdf-panel-head">
                  <h4 className="vn-pdf-panel-title">Full PDF Notes</h4>
                  <p className="vn-pdf-panel-sub">Scroll to read all pages directly on this course page.</p>
                </div>
                <PdfViewer pdfUrl={pdfUrl} filename={pdfName} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   COMMENT CARD
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function CommentCard({ review, isOwn }) {
  const [showFull, setShowFull] = useState(false);
  const long = review.comment?.length > 200;
  const text = long && !showFull ? review.comment.slice(0, 200) + "â€¦" : review.comment;

  return (
    <article className={`vc-card ${isOwn ? "vc-card--mine" : ""}`}>
      <div className="vc-header">
        <div className="vc-avatar"
          style={{ background: `hsl(${((review.user_name || "?").charCodeAt(0) * 37) % 360},55%,55%)` }}>
          {(review.user_name || "?")[0].toUpperCase()}
        </div>
        <div className="vc-meta">
          <div className="vc-name">
            {review.user_name || "Student"}
            {isOwn && <span className="vc-you-badge">You</span>}
          </div>
          {review.created_at && <div className="vc-time">{formatTimeAgo(review.created_at)}</div>}
        </div>
        <div className="vc-stars">
          <span className="vc-star-glyph">{toStars(review.rating)}</span>
          <span className="vc-score">{Number(review.rating || 0).toFixed(1)}</span>
        </div>
      </div>
      {review.comment && (
        <div className="vc-body">
          <p className="vc-comment">{text}</p>
          {long && (
            <button className="vc-read-more" onClick={() => setShowFull(!showFull)}>
              {showFull ? "Show less" : "Read more"}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   ADMIN VIDEO MANAGER
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
function AdminVideoManager({ video, courseId, onSaved }) {
  const currentType = resolveVideoType(video);
  const [mode, setMode]     = useState(currentType === "youtube" ? "youtube" : "upload");
  const [ytLink, setYtLink] = useState(currentType === "youtube" ? (video?.video_url || "") : "");
  const [extLink, setExtLink] = useState(currentType === "link" ? (video?.video_url || "") : "");
  const [file, setFile]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [progress, setProgress] = useState(0);
  const [msg, setMsg]       = useState({ text: "", type: "" });
  const fileRef = useRef(null);
  const ytPreviewId = getYouTubeId(ytLink);

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 5000);
  };

  const putJson = async (body) => {
    const res = await fetch(`/api/courses/${courseId}/videos/${video.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAuthToken()}` },
      body: JSON.stringify({
        title: video?.title || "Lesson", description: video?.description || "",
        is_intro: video?.is_intro ? "1" : "0", is_active: video?.is_active !== false ? "1" : "0",
        sort_order: String(video?.sort_order || 0), ...body
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) throw new Error(data.error || "Request failed");
    return data;
  };

  const handleUploadFile = async () => {
    if (!file) { showMsg("Please select a video file first.", "error"); return; }
    setBusy(true); setProgress(0);
    const fd = new FormData();
    fd.append("title", video?.title || "Lesson");
    fd.append("description", video?.description || "");
    fd.append("video_type", "upload");
    fd.append("is_intro", video?.is_intro ? "1" : "0");
    fd.append("is_active", video?.is_active !== false ? "1" : "0");
    fd.append("sort_order", String(video?.sort_order || 0));
    fd.append("video_file", file);
    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", `/api/courses/${courseId}/videos/${video.id}`);
        xhr.setRequestHeader("Authorization", `Bearer ${getAuthToken()}`);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round(e.loaded / e.total * 100)); };
        xhr.onload = () => {
          try {
            const d = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300 && d.success) resolve(d);
            else reject(new Error(d.error || "Upload failed"));
          } catch { reject(new Error("Invalid server response")); }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(fd);
      });
      showMsg("Video uploaded successfully!", "success");
      setFile(null); if (fileRef.current) fileRef.current.value = ""; setProgress(0);
      onSaved?.();
    } catch (err) { showMsg(err.message || "Upload failed.", "error"); }
    finally { setBusy(false); }
  };

  const handleSaveYouTube = async () => {
    if (!ytPreviewId) { showMsg("Please enter a valid YouTube URL.", "error"); return; }
    setBusy(true);
    try { await putJson({ video_type: "youtube", video_url: ytLink.trim() }); showMsg("YouTube link saved!", "success"); onSaved?.(); }
    catch (err) { showMsg(err.message, "error"); }
    finally { setBusy(false); }
  };

  const handleSaveLink = async () => {
    if (!extLink.trim()) { showMsg("Please enter a valid URL.", "error"); return; }
    setBusy(true);
    try { await putJson({ video_type: "link", video_url: extLink.trim() }); showMsg("Video link saved!", "success"); onSaved?.(); }
    catch (err) { showMsg(err.message, "error"); }
    finally { setBusy(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove this video? Students won't be able to watch until you add one back.")) return;
    setBusy(true);
    try { await putJson({ video_type: "none", video_url: "" }); showMsg("Video removed.", "info"); onSaved?.(); }
    catch (err) { showMsg(err.message, "error"); }
    finally { setBusy(false); }
  };

  const currentBadge =
    currentType === "youtube" ? { label: "â–¶ YouTube", cls: "yt" }
    : currentType === "upload" ? { label: "ðŸ“ Uploaded File", cls: "upload" }
    : currentType === "link"   ? { label: "ðŸ”— Direct URL", cls: "link" }
    : { label: "âš  No Video", cls: "none" };

  return (
    <div className="avm-root">
      <div className="avm-header">
        <div className="avm-header-left">
          <span className="avm-admin-icon">ðŸŽ¬</span>
          <div>
            <div className="avm-title">Manage Lesson Video</div>
            <div className="avm-subtitle">{video?.title || "This Lesson"}</div>
          </div>
        </div>
        <span className={`avm-badge avm-badge--${currentBadge.cls}`}>{currentBadge.label}</span>
      </div>

      {msg.text && (
        <div className={`avm-toast avm-toast--${msg.type}`}>
          <span className="avm-toast-icon">{msg.type === "success" ? "âœ“" : msg.type === "error" ? "âœ•" : "â„¹"}</span>
          {msg.text}
        </div>
      )}

      <div className="avm-mode-bar">
        {[
          { id: "upload",  icon: "ðŸ“", label: "Upload File",     sub: "MP4/WebM/MOV" },
          { id: "youtube", icon: "â–¶",  label: "YouTube Link",    sub: "youtu.be/..." },
          { id: "link",    icon: "ðŸ”—", label: "Direct Video URL", sub: "cdn.example.com" },
        ].map((m) => (
          <button key={m.id}
            className={`avm-mode-btn ${mode === m.id ? "avm-mode-btn--on" : ""}`}
            onClick={() => setMode(m.id)} disabled={busy}>
            <span className="avm-mode-icon">{m.icon}</span>
            <div className="avm-mode-text">
              <span className="avm-mode-label">{m.label}</span>
              <span className="avm-mode-sub">{m.sub}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Upload panel */}
      {mode === "upload" && (
        <div className="avm-panel">
          <p className="avm-panel-hint">
            Drag & drop or click to browse. Supported: <strong>MP4, WebM, MOV, AVI, MKV</strong> Â· Max 1 GB
          </p>
          <div className="avm-dropzone"
            onClick={() => !busy && fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f && f.type.startsWith("video/")) setFile(f); }}>
            {file ? (
              <div className="avm-file-row">
                <div className="avm-file-thumb">ðŸŽ¬</div>
                <div className="avm-file-info">
                  <p className="avm-file-name">{file.name}</p>
                  <p className="avm-file-meta">{(file.size / 1024 / 1024).toFixed(1)} MB Â· {file.type}</p>
                </div>
                <button className="avm-file-clear" onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>âœ•</button>
              </div>
            ) : (
              <div className="avm-drop-placeholder">
                <div className="avm-drop-icon">ðŸ“</div>
                <p className="avm-drop-text">Click to browse or drag & drop</p>
                <p className="avm-drop-formats">MP4 Â· WebM Â· MOV Â· AVI Â· MKV</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="video/*" style={{ display: "none" }}
            onChange={(e) => setFile(e.target.files[0] || null)} />
          {busy && (
            <div className="avm-progress-area">
              <div className="avm-progress-bar"><div className="avm-progress-fill" style={{ width: `${progress}%` }} /></div>
              <span className="avm-progress-text">{progress < 100 ? `Uploading ${progress}%â€¦` : "Processingâ€¦"}</span>
            </div>
          )}
          <div className="avm-footer">
            <button className="avm-btn avm-btn--primary" onClick={handleUploadFile} disabled={busy || !file}>
              {busy ? `Uploading ${progress}%â€¦` : "Upload & Save Video"}
            </button>
            {video?.video_url && <button className="avm-btn avm-btn--ghost" onClick={handleRemove} disabled={busy}>Remove Current Video</button>}
          </div>
        </div>
      )}

      {/* YouTube panel */}
      {mode === "youtube" && (
        <div className="avm-panel">
          <p className="avm-panel-hint">Paste any YouTube URL â€” share link, full URL, shorts, or embed.</p>
          <div className="avm-input-row">
            <span className="avm-input-icon">â–¶</span>
            <input className="avm-input" type="url" placeholder="https://youtu.be/xxxx"
              value={ytLink} onChange={(e) => setYtLink(e.target.value)} disabled={busy} />
          </div>
          {ytLink && !ytPreviewId && <p className="avm-input-error">âš  Could not detect a valid YouTube video ID.</p>}
          {ytPreviewId && (
            <div className="avm-yt-preview">
              <p className="avm-preview-label">Live Preview â€” what students will see:</p>
              <div style={{ position: "relative", aspectRatio: "16/9", borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}>
                <iframe src={`https://www.youtube.com/embed/${ytPreviewId}?rel=0&modestbranding=1`}
                  title="YouTube Preview"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }} />
              </div>
            </div>
          )}
          <div className="avm-footer">
            <button className="avm-btn avm-btn--primary" onClick={handleSaveYouTube} disabled={busy || !ytPreviewId}>
              {busy ? "Savingâ€¦" : "Save YouTube Link"}
            </button>
            {video?.video_url && <button className="avm-btn avm-btn--ghost" onClick={handleRemove} disabled={busy}>Remove Current Video</button>}
          </div>
        </div>
      )}

      {/* Direct URL panel */}
      {mode === "link" && (
        <div className="avm-panel">
          <p className="avm-panel-hint">Paste a direct MP4/WebM URL hosted on a CDN or external server.</p>
          <div className="avm-input-row">
            <span className="avm-input-icon">ðŸ”—</span>
            <input className="avm-input" type="url" placeholder="https://cdn.example.com/video.mp4"
              value={extLink} onChange={(e) => setExtLink(e.target.value)} disabled={busy} />
          </div>
          {extLink && (
            <div className="avm-link-preview">
              <span className="avm-link-icon">ðŸ”—</span>
              <a href={extLink} target="_blank" rel="noreferrer" className="avm-link-url">{extLink}</a>
            </div>
          )}
          <div className="avm-footer">
            <button className="avm-btn avm-btn--primary" onClick={handleSaveLink} disabled={busy || !extLink.trim()}>
              {busy ? "Savingâ€¦" : "Save Video URL"}
            </button>
            {video?.video_url && <button className="avm-btn avm-btn--ghost" onClick={handleRemove} disabled={busy}>Remove Current Video</button>}
          </div>
        </div>
      )}
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   MAIN PAGE
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
export default function CourseMainPage() {
  const { courseId } = useParams();
  const navigate     = useNavigate();

  const [course, setCourse]       = useState(null);
  const [videos, setVideos]       = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [reviews, setReviews]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState("");
  const [notice, setNotice]       = useState("");
  const [noticeType, setNoticeType] = useState("info");
  const [rating, setRating]       = useState(5);
  const [comment, setComment]     = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(!!getToken());
  const [loginOpen, setLoginOpen] = useState(false);
  const [autoPlayNext, setAutoPlayNext] = useState(true);

  const isAdmin = useMemo(() => {
    try {
      const raw  = localStorage.getItem("th_user") || localStorage.getItem("user");
      if (!raw) return false;
      const role = String(JSON.parse(raw)?.role || "").toLowerCase();
      return role === "admin" || role === "instructor";
    } catch { return false; }
  }, []);

  const currentUserId = useMemo(() => readCurrentUserId(), []);

  const showNotice = (m, type = "info") => {
    setNotice(m); setNoticeType(type);
    setTimeout(() => setNotice(""), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [courseRes, reviewRes, videosRes] = await Promise.all([
        coursesApi.getById(courseId),
        coursesApi.getReviews(courseId),
        coursesApi.getVideos ? coursesApi.getVideos(courseId) : Promise.resolve({ videos: [] }),
      ]);
      const c  = courseRes?.course || null;
      const vv = videosRes?.videos || [];
      const rr = reviewRes?.reviews || [];
      setCourse(c); setReviews(rr);
      const finalVideos = vv.length > 0 ? vv : buildDemoVideos(c);
      setVideos(finalVideos);
      setActiveVideo((prev) => {
        if (prev) { const still = finalVideos.find((v) => v.id === prev.id); if (still) return still; }
        return finalVideos[0] || null;
      });
    } catch (err) { setError(err?.message || "Failed to load course."); }
    finally { setLoading(false); }
  }, [courseId]);

  useEffect(() => { load(); }, [load, isLoggedIn]);

  function buildDemoVideos(c) {
    if (!c) return [];
    const lessons = [
      "Introduction & Overview", "Setting Up Your Environment", "Core Concepts Explained",
      "Hands-On Practice", "Advanced Techniques", "Project Walkthrough",
      "Common Mistakes to Avoid", "Summary & Next Steps",
    ];
    return lessons.map((t, i) => ({
      id: `demo-${i}`, title: t,
      description: `${t}. ${c.description || ""}`.trim(),
      thumbnail: c.banner || c.image || c.cover || null,
      video_url: c.video_url || c.intro_video || "",
      video_type: "", duration_seconds: 600 + i * 240,
      views: Math.floor(Math.random() * 5000 + 200),
      rating: Number((3.8 + Math.random() * 1.2).toFixed(1)),
      created_at: new Date(Date.now() - i * 7 * 86400000).toISOString(),
      notes: c.notes || "", notes_pdf: c.notes_pdf || "", sort_order: i,
    }));
  }

  const selectVideo = (v) => { setActiveVideo(v); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const handleVideoEnded = () => {
    if (!autoPlayNext) return;
    const idx  = videos.findIndex((v) => v.id === activeVideo?.id);
    const next = videos[idx + 1]; if (next) selectVideo(next);
  };

  const handleEnroll = async () => {
    if (!isLoggedIn) return setLoginOpen(true);
    if (!course?.id || course?.isEnrolled) { showNotice("Already enrolled!", "info"); return; }
    setBusy(true);
    try { await enrollmentsApi.enroll(course.id); showNotice("Enrolled successfully!", "success"); await load(); }
    catch (err) {
      const m = String(err?.message || "");
      if (m.toLowerCase().includes("unauthorized")) setLoginOpen(true);
      else showNotice(m || "Enrollment failed.", "error");
    } finally { setBusy(false); }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) return setLoginOpen(true);
    if (!course?.isEnrolled) { showNotice("Enroll first to review.", "warning"); return; }
    const sel = Number(rating);
    if (!Number.isFinite(sel) || sel < 1 || sel > 5) { showNotice("Select 1â€“5.", "warning"); return; }
    setBusy(true);
    try { await coursesApi.submitReview(course.id, sel, comment); setComment(""); showNotice("Review submitted!", "success"); await load(); }
    catch (err) {
      const m = String(err?.message || "");
      if (m.toLowerCase().includes("unauthorized")) setLoginOpen(true);
      else showNotice(m || "Failed to submit.", "error");
    } finally { setBusy(false); }
  };

  const handleDeleteReview = async () => {
    if (!isLoggedIn || !course?.id) return;
    setBusy(true);
    try { await coursesApi.deleteReview(course.id); showNotice("Review removed.", "info"); await load(); }
    catch (err) { showNotice(String(err?.message || "Failed."), "error"); }
    finally { setBusy(false); }
  };

  if (loading) return (
    <div className="vp-root">
      <div className="vp-loading"><div className="vp-loader-ring" /><p>Loading courseâ€¦</p></div>
    </div>
  );
  if (error || !course) return (
    <div className="vp-root"><div className="vp-error">
      <div className="vp-error-icon">âœ•</div>
      <h2>Course not found</h2>
      <p>{error || "We couldn't load this course."}</p>
      <button className="vp-btn vp-btn--outline" onClick={() => navigate(-1)}>â† Go back</button>
    </div></div>
  );

  const myReview     = reviews.find((r) => Number(r.user_id) === Number(currentUserId));
  const avgRating    = Number(course.rating || 0);
  const reviewCount  = Number(course.reviews || 0);
  const enrolledCount = Number(course.enrolled || 0);
  const activeIdx    = videos.findIndex((v) => v.id === activeVideo?.id);
  const vType        = resolveVideoType(activeVideo);
  const activePdfUrl = isPdfAsset(activeVideo?.video_url) ? String(activeVideo?.video_url || "").trim() : "";
  const instructorName = getInstructorField(course?.instructor, "name") || "Instructor";
  const instructorTitle = getInstructorField(course?.instructor, "title") || "Course Instructor";
  return (
    <div className="vp-root">
      {notice && (
        <div className={`vp-toast vp-toast--${noticeType}`}>
          <span>{noticeType === "success" ? "âœ“" : noticeType === "error" ? "âœ•" : "â„¹"}</span> {notice}
        </div>
      )}

      <div className="vp-layout">
        {/* â”€â”€ LEFT â”€â”€ */}
        <div className="vp-left">
          {activePdfUrl ? (
            <section className="vp-primary-notes">
              <h2 className="vp-primary-notes-title">Lesson Notes</h2>
              <p className="vp-primary-notes-subtitle">This lesson uses PDF notes. Scroll to read the full document.</p>
              <PdfViewer
                pdfUrl={activePdfUrl}
                filename={`${String(activeVideo?.title || "Lesson").trim() || "Lesson"} - Notes.pdf`}
              />
            </section>
          ) : (
            <VideoPlayer
              key={activeVideo?.id || "empty"}
              video={activeVideo}
              poster={activeVideo?.thumbnail || course.banner || ""}
              onEnded={handleVideoEnded}
              autoPlay={activeIdx > 0}
            />
          )}

          <div className="vp-title-row">
            <div>
              <h1 className="vp-video-title">{String(activeVideo?.title || course.title || "")}</h1>
              <div className="vp-video-meta">
                {(activeVideo?.views || 0) > 0 && <span>{Number(activeVideo.views).toLocaleString("en-IN")} views</span>}
                {activeVideo?.created_at && <span>Â· {formatTimeAgo(activeVideo.created_at)}</span>}
                {(activeVideo?.rating || 0) > 0 && (
                  <span className="vp-video-rating">â­ {Number(activeVideo.rating).toFixed(1)}</span>
                )}
                {activeVideo?.video_url && (
                  <span className={`vp-type-badge vp-type-badge--${vType}`}>
                    {vType === "youtube" ? "YouTube" : vType === "upload" ? "Uploaded" : vType === "link" ? "Linked" : ""}
                  </span>
                )}
              </div>
              {activeVideo && (
                <div className="vp-lesson-progress">
                  <span className="vp-progress-chip">
                    {activeIdx >= 0 ? `Lesson ${activeIdx + 1} of ${videos.length}` : "Now playing"}
                  </span>
                  <label className="vp-autoplay-toggle">
                    <input type="checkbox" checked={autoPlayNext} onChange={(e) => setAutoPlayNext(e.target.checked)} />
                    <span className="vp-autoplay-track" />
                    <span className="vp-autoplay-label">Autoplay Next</span>
                  </label>
                </div>
              )}
            </div>
            <div>
              {course.isEnrolled
                ? <span className="vp-enrolled-badge">âœ“ Enrolled</span>
                : <button className="vp-btn vp-btn--enroll" onClick={handleEnroll} disabled={busy}>
                    {course.price > 0 ? `Enroll Â· â‚¹${Number(course.price).toLocaleString("en-IN")}` : "Enroll Free"}
                  </button>}
            </div>
          </div>

          {/* Admin panel â€” only for real lessons */}
          {isAdmin && activeVideo && !String(activeVideo.id).startsWith("demo-") && (
            <AdminVideoManager video={activeVideo} courseId={courseId} onSaved={load} />
          )}

          {/* Notes / Description / PDF â€” conditional tabs */}
          <NotesSection video={activeVideo} course={course} />

          {/* Reviews */}
          <section className="vc-section">
            <div className="vc-section-hdr">
              <h2 className="vc-section-title">
                Reviews &amp; Comments <span className="vc-count">{reviews.length}</span>
              </h2>
              {avgRating > 0 && (
                <div className="vc-avg">
                  <span className="vc-avg-num">{avgRating.toFixed(1)}</span>
                  <span className="vc-avg-stars">{toStars(avgRating)}</span>
                  <span className="vc-avg-sub">{reviewCount} ratings</span>
                </div>
              )}
            </div>

            {!course.isEnrolled ? (
              <div className="vc-enroll-prompt">
                <span>Enroll to leave a review.</span>
                <button className="vp-btn vp-btn--sm vp-btn--primary" onClick={handleEnroll} disabled={busy}>
                  Enroll now
                </button>
              </div>
            ) : (
              <form className="vc-form" onSubmit={handleSubmitReview}>
                <div className="vc-form-row">
                  <label className="vc-form-label">Your rating</label>
                  <StarPicker value={rating} onChange={setRating} disabled={busy} />
                </div>
                <textarea className="vc-textarea" value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3} placeholder="Share what you found most usefulâ€¦" disabled={busy} />
                <div className="vc-form-actions">
                  <button className="vp-btn vp-btn--primary" type="submit" disabled={busy}>
                    {myReview ? "Update Review" : "Submit Review"}
                  </button>
                  {myReview && (
                    <button className="vp-btn vp-btn--danger" type="button" onClick={handleDeleteReview} disabled={busy}>
                      Delete
                    </button>
                  )}
                </div>
              </form>
            )}

            {reviews.length === 0
              ? <div className="vc-empty"><span>â—Ž</span><p>No reviews yet â€” be the first!</p></div>
              : <div className="vc-list">
                  {reviews.map((r) => (
                    <CommentCard key={r.id} review={r} isOwn={Number(r.user_id) === Number(currentUserId)} />
                  ))}
                </div>}
          </section>
        </div>

        {/* â”€â”€ RIGHT â€” Playlist sidebar â”€â”€ */}
        <aside className="vp-right">
          <div className="vq-header">
            <h3 className="vq-heading">
              <span className="vq-heading-main">Course Lessons</span>
              <span className="vq-heading-sub">{instructorName}</span>
              <span className="vq-heading-role">{instructorTitle}</span>
              <span className="vq-count">{videos.length} lessons</span>
            </h3>
            <div className="vq-course-meta">
              <span>â­ {avgRating.toFixed(1)}</span>
              <span>Â· {enrolledCount.toLocaleString("en-IN")} students</span>
            </div>
          </div>
          <div className="vq-list">
            {videos.map((v, i) => (
              <VideoQueueItem
                key={v.id} video={v} index={i}
                active={v.id === activeVideo?.id}
                onSelect={selectVideo}
                courseBanner={course?.banner || null}
              />
            ))}
          </div>
        </aside>
      </div>

      <Login isOpen={loginOpen} onClose={() => setLoginOpen(false)}
        onLoginSuccess={() => { setLoginOpen(false); setIsLoggedIn(true); }} />
    </div>
  );
}

